import React from 'react';
import { Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { MuiButton } from '@ace/ace-ui-web';
import moment from 'moment';
import { View } from 'react-big-calendar';

interface CalendarCustomToolbarProps {
    date: Date;
    view: View;
    onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY') => void;
    onView: (view: View) => void;
    filterSlot?: React.ReactNode;
}

const CalendarCustomToolbar: React.FC<CalendarCustomToolbarProps> = ({ date, view, onNavigate, onView, filterSlot }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const goToBack = () => onNavigate('PREV');
    const goToNext = () => onNavigate('NEXT');
    const goToCurrent = () => onNavigate('TODAY');

    const label = () => {
        const momentDate = moment(date);
        if (view === 'month') {
            return momentDate.format('MMMM YYYY');
        } else if (view === 'week') {
            const start = momentDate.clone().startOf('week');
            const end = momentDate.clone().endOf('week');
            return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
        } else if (view === 'day') {
            return momentDate.format('dddd, MMMM D, YYYY');
        }
        return momentDate.format('MMMM YYYY');
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 1, sm: 0 },
            mb: 2,
            pb: 1.5,
            borderBottom: '1px solid #e0e0e0'
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton
                    onClick={goToBack}
                    size="small"
                    sx={{ color: '#CC0000', '&:hover': { backgroundColor: 'rgba(204, 0, 0, 0.08)' } }}
                >
                    <ChevronLeftIcon />
                </IconButton>

                <MuiButton
                    onClick={goToCurrent}
                    colors="acePrimary"
                    size="small"
                    sx={{ textTransform: 'uppercase', fontWeight: 600, py: 0.5 }}
                >
                    TODAY
                </MuiButton>

                <IconButton
                    onClick={goToNext}
                    size="small"
                    sx={{ color: '#CC0000', '&:hover': { backgroundColor: 'rgba(204, 0, 0, 0.08)' } }}
                >
                    <ChevronRightIcon />
                </IconButton>

                <Typography variant="h6" sx={{ fontWeight: 600, ml: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {label()}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: { xs: 'flex-end', sm: 'auto' } }}>
                {filterSlot}
                <Box sx={{ display: 'flex', gap: 0 }}>
                    <MuiButton
                        onClick={() => onView('month')}
                        size="small"
                        colors={view === 'month' ? 'acePrimary' : 'secondary'}
                        sx={{ borderRadius: '4px 0 0 4px', py: 0.5 }}
                    >
                        {isMobile ? 'M' : 'MONTH'}
                    </MuiButton>
                    <MuiButton
                        onClick={() => onView('week')}
                        size="small"
                        colors={view === 'week' ? 'acePrimary' : 'secondary'}
                        sx={{ borderRadius: 0, py: 0.5 }}
                    >
                        {isMobile ? 'W' : 'WEEK'}
                    </MuiButton>
                    <MuiButton
                        onClick={() => onView('day')}
                        size="small"
                        colors={view === 'day' ? 'acePrimary' : 'secondary'}
                        sx={{ borderRadius: '0 4px 4px 0', py: 0.5 }}
                    >
                        {isMobile ? 'D' : 'DAY'}
                    </MuiButton>
                </Box>
            </Box>
        </Box>
    );
};

export default CalendarCustomToolbar;